// BDSJEA-312: mensaje descriptivo de errores de la API para el usuario.
//
// Regla de negocio: el front toma el mensaje que devuelve el handler de la API
// y lo muestra; nunca un error generico ni el texto crudo de axios
// ("Request failed with status code 504", "Network Error", "timeout of 0ms
// exceeded"). Este modulo es puro (sin React, sin axios) y lo usan el
// interceptor de respuesta (auth_helpers.js), toastHandler y las pantallas.
//
// Formatos de cuerpo de error tolerados (el contrato nuevo lo fija BDSJEA-311):
//   { message }                      ewan-auth / contrato nuevo
//   { mensaje }                      ewan-agro (GlobalExceptionHandler)
//   { apierror: { message, subErrors } }   ewan-api
//   { timestamp, status, error, path }     Spring por defecto (sin texto util)
//   HTML de nginx, string o cuerpo vacio   => "sin cuerpo"

const MSG = {
  badRequest: "La solicitud no es válida.",
  unauthorized: "Tu sesión expiró. Volvé a iniciar sesión.",
  unauthorizedSinSesion: "No se pudo autenticar la solicitud.",
  forbidden: "No tenés permisos para realizar esta operación.",
  notFound: "No se encontró el recurso solicitado.",
  conflict: "La operación entra en conflicto con datos existentes.",
  unprocessable: "Los datos enviados no son válidos.",
  server:
    "Ocurrió un error interno en el servidor. Intentá nuevamente; si persiste, contactá a soporte.",
  unavailable:
    "El servicio no está disponible. Intentá nuevamente en unos minutos.",
  timeout: "El servidor no respondió a tiempo, intentá nuevamente.",
  network: "No hay conexión con el servidor.",
  generic: "No se pudo completar la operación.",
  unexpected: "Error inesperado",
}

// Frases estandar de HTTP que Spring/nginx ponen en `error`: no son un mensaje
// para el usuario.
const REASON_PHRASES = new Set([
  "bad request",
  "unauthorized",
  "forbidden",
  "not found",
  "method not allowed",
  "not acceptable",
  "request timeout",
  "conflict",
  "gone",
  "unprocessable entity",
  "too many requests",
  "internal server error",
  "not implemented",
  "bad gateway",
  "service unavailable",
  "gateway timeout",
  "gateway time-out",
  "no message available",
])

const TIMEOUT_CODES = ["ECONNABORTED", "ETIMEDOUT"]
const NETWORK_CODES = ["ERR_NETWORK"]
const CANCEL_CODES = ["ERR_CANCELED"]

// Texto crudo que genera axios; nunca se muestra al usuario.
const CRUDE_AXIOS_MESSAGE =
  /^(Request failed with status code \d+|Network Error|timeout of \d+ms exceeded|Request aborted)$/i

const isObject = value => value !== null && typeof value === "object"

const esTextoUtil = value =>
  typeof value === "string" &&
  value.trim() !== "" &&
  !REASON_PHRASES.has(value.trim().toLowerCase())

const esHtml = value =>
  typeof value === "string" && /^\s*<(!doctype|html|head|body)/i.test(value)

/** true si el error es una cancelacion (AbortController / axios.CancelToken). */
export function isCancelledError(error) {
  if (!isObject(error)) return false
  return (
    error.__CANCEL__ === true ||
    error.name === "CanceledError" ||
    CANCEL_CODES.includes(error.code)
  )
}

/** true si el error proviene de axios (o imita su forma: code/response/request). */
function isAxiosLike(error) {
  if (!isObject(error)) return false
  return (
    error.isAxiosError === true ||
    isObject(error.response) ||
    TIMEOUT_CODES.includes(error.code) ||
    NETWORK_CODES.includes(error.code)
  )
}

function leerHeader(headers, nombre) {
  if (!isObject(headers)) return undefined
  const clave = Object.keys(headers).find(k => k.toLowerCase() === nombre)
  return clave ? headers[clave] : undefined
}

function leerCorrelationId(response) {
  const data = response?.data
  const delBody = isObject(data)
    ? data.correlationId || data.traceId || data.requestId
    : undefined
  return (
    delBody ||
    leerHeader(response?.headers, "x-correlation-id") ||
    leerHeader(response?.headers, "x-request-id") ||
    undefined
  )
}

// Devuelve el texto util del cuerpo o null si no hay (HTML, string, vacio o
// solo la frase estandar de HTTP).
function leerMensajeDelBody(data) {
  if (!isObject(data) || esHtml(data)) return null
  const candidatos = [data.message, data.mensaje, data.apierror?.message]
  for (const candidato of candidatos) {
    if (esTextoUtil(candidato)) return candidato.trim()
  }
  if (esTextoUtil(data.error)) return data.error.trim()
  return null
}

function leerErroresPorCampo(data) {
  if (!isObject(data)) return null
  const lista = data.apierror?.subErrors || data.subErrors || data.errors
  if (!Array.isArray(lista)) return null
  const mapa = {}
  lista.forEach(sub => {
    if (sub?.field && sub?.message) mapa[sub.field] = sub.message
  })
  return Object.keys(mapa).length ? mapa : null
}

function haySesion() {
  try {
    return !!localStorage.getItem("authUser")
  } catch {
    return false
  }
}

function mensajePorStatus(status) {
  switch (status) {
    case 400:
      return MSG.badRequest
    case 401:
      return haySesion() ? MSG.unauthorized : MSG.unauthorizedSinSesion
    case 403:
      return MSG.forbidden
    case 404:
      return MSG.notFound
    case 408:
    case 504:
      return MSG.timeout
    case 409:
      return MSG.conflict
    case 422:
      return MSG.unprocessable
    case 502:
    case 503:
      return MSG.unavailable
    default:
      return status >= 500 ? MSG.server : MSG.generic
  }
}

/**
 * Analiza un error (axios o no) y devuelve toda la informacion necesaria para
 * mostrarlo. Tolera undefined, strings, objetos planos y errores sin response.
 *
 * @returns {{
 *   cancelled: boolean, message: string, status: (number|null),
 *   code: (string|null), correlationId: (string|undefined),
 *   hasBody: boolean, kind: string, fieldErrors: (Object|null)
 * }}
 */
export function parseApiError(error) {
  const base = {
    cancelled: false,
    message: MSG.unexpected,
    status: null,
    code: null,
    correlationId: undefined,
    hasBody: false,
    kind: "unknown",
    fieldErrors: null,
  }

  if (error === undefined || error === null) return base
  if (typeof error === "string") {
    return { ...base, message: error.trim() || MSG.unexpected, kind: "text" }
  }
  if (!isObject(error)) return base

  if (isCancelledError(error)) {
    return { ...base, cancelled: true, message: "", kind: "cancelled" }
  }

  const response = isObject(error.response) ? error.response : null
  const status = Number.isFinite(response?.status) ? response.status : null
  const code = typeof error.code === "string" ? error.code : null
  const correlationId = leerCorrelationId(response)
  const fieldErrors = leerErroresPorCampo(response?.data)
  const common = { ...base, status, code, correlationId, fieldErrors }

  // Con cuerpo util de la API: se muestra tal cual (formatos nuevo y viejos).
  const delBody = response ? leerMensajeDelBody(response.data) : null
  if (delBody) {
    return { ...common, message: delBody, hasBody: true, kind: "api" }
  }

  if (response) {
    // Respuesta HTTP sin cuerpo util (nginx, Spring por defecto, vacio).
    let message = mensajePorStatus(status)
    const esServidor = status >= 500
    if (esServidor) {
      const detalle = [`HTTP ${status}`]
      if (correlationId) detalle.push(`Código: ${correlationId}`)
      message = `${message} (${detalle.join(" - ")})`
    }
    return { ...common, message, kind: esServidor ? "server" : "http" }
  }

  // Sin respuesta: timeout / red caida.
  if (TIMEOUT_CODES.includes(code)) {
    return { ...common, message: MSG.timeout, kind: "timeout" }
  }
  if (NETWORK_CODES.includes(code) || isAxiosLike(error)) {
    return { ...common, message: MSG.network, kind: "network" }
  }

  // Error comun de JS (p.ej. new Error("boom") o un throw propio de la app):
  // su mensaje es intencional, salvo que sea texto crudo de axios.
  if (esTextoUtil(error.message) && !CRUDE_AXIOS_MESSAGE.test(error.message)) {
    return { ...common, message: error.message.trim(), kind: "app" }
  }
  return { ...common, message: MSG.generic, kind: "app" }
}

/**
 * Mensaje listo para mostrar. Devuelve "" si el error es una cancelacion (el
 * llamador no debe avisar nada).
 *
 * @param {*} error
 * @param {{prefix?: string, fallback?: string}} [opciones]
 *   prefix: texto previo, se concatena como "prefix: mensaje".
 *   fallback: mensaje si el error no trae nada utilizable (undefined).
 */
export function getErrorMessage(error, { prefix, fallback } = {}) {
  const info = parseApiError(error)
  if (info.cancelled) return ""
  const mensaje = info.kind === "unknown" && fallback ? fallback : info.message
  return prefix ? `${prefix}: ${mensaje}` : mensaje
}

/**
 * Normaliza un error de axios en el interceptor: reescribe `error.message` con
 * el texto para el usuario y deja `error.response` intacto. Idempotente y no
 * toca las cancelaciones ni lo que no es un objeto.
 */
export function normalizeApiError(error) {
  if (!isObject(error) || error.errorInfo) return error
  const info = parseApiError(error)
  if (info.cancelled) return error
  error.errorInfo = info
  error.userMessage = info.message
  if (info.correlationId) error.correlationId = info.correlationId
  error.message = info.message
  return error
}
